import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';

export interface DealerPayment {
  id: number;
  paymentCode: string;
  dealerId: number;
  entryType: string;
  direction: string;
  amount: number;
  referenceNo: string | null;
  paymentMode: string | null;
  utrNumber: string | null;
  paymentDate: string | null;
  reconciliationStatus: string;
  reconciledAt: string | null;
  reconciledBy: number | null;
  narration: string | null;
  regionId: number | null;
  createdAt: string;
  transactionNumber: string | null;
  amountPaid: number | null;
  remarks: string | null;
  paymentProofUrl: string | null;
  dueDate: string | null;
  acceptedAt: string | null;
  acceptedBy: number | null;
}

export interface MarkPaidInput {
  paymentDate: string;
  paymentMode: string;
  amountPaid: number;
  utrNumber?: string;
  transactionNumber?: string;
  remarks?: string;
  paymentProofUrl?: string;
}

export interface Bucket { label: string; value: number }

export interface CreatePaymentInput {
  dealerId: number;
  entryType: string;
  direction: string;
  amount: number;
  referenceNo?: string;
  paymentMode?: string;
  utrNumber?: string;
  paymentDate?: string;
  narration?: string;
  regionId?: number | null;
}

export interface ReconciliationSummary {
  totalDebit: number;
  totalCredit: number;
  outstanding: number;
  unreconciled: { count: number; amount: number };
  reconciled: { count: number; amount: number };
  disputed: { count: number; amount: number };
  recentUnreconciled: DealerPayment[];
  // Phase 3 enriched metrics
  totalInvoiceValue: number;
  paidAmount: number;
  outstandingAmount: number;
  overdueAmount: number;
  creditBalance: number;
  ageing: Bucket[];
  invoiceStatusSummary: Bucket[];
  invoiceTrend: Bucket[];
  paymentTrend: Bucket[];
}

const base = createCrudApi<DealerPayment, CreatePaymentInput>('dealer-payments');

export const dealerPaymentsApi = {
  ...base,
  async reconciliation(): Promise<ReconciliationSummary> {
    const { data } = await apiClient.get<ApiSuccess<ReconciliationSummary>>('/dealer-payments/reconciliation');
    return data.data;
  },
  async reconcile(
    id: number,
    input: { reconciliationStatus: 'reconciled' | 'disputed'; utrNumber?: string; paymentDate?: string; narration?: string },
  ): Promise<DealerPayment> {
    const { data } = await apiClient.post<ApiSuccess<DealerPayment>>(`/dealer-payments/${id}/reconcile`, input);
    return data.data;
  },
  async accept(id: number): Promise<DealerPayment> {
    const { data } = await apiClient.post<ApiSuccess<DealerPayment>>(`/dealer-payments/${id}/accept`, {});
    return data.data;
  },
  async markPaid(id: number, input: MarkPaidInput): Promise<DealerPayment> {
    const { data } = await apiClient.post<ApiSuccess<DealerPayment>>(`/dealer-payments/${id}/mark-paid`, input);
    return data.data;
  },
  async download(id: number, format: 'xlsx' | 'csv' | 'pdf' = 'pdf'): Promise<void> {
    const res = await apiClient.get(`/dealer-payments/${id}/download?format=${format}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${id}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
